
import { type FieldValues, type UseFormReturn, useForm, FormProvider, type DefaultValues } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import type { z } from "zod"
import { Button } from "@/components/ui/button"
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Spinner } from "components"
import { useEffect } from "react"

interface ModalFormProps<T extends FieldValues> {
  title: string
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: T) => void
  schema?: z.ZodType<T>
  defaultValues?: DefaultValues<T>;
  children: (form: UseFormReturn<T>) => React.ReactNode;
  submitButtonText?: string;
  isLoading?: boolean;
}

export function ModalForm<T extends FieldValues>({
  title,
  isOpen,
  onClose,
  onSubmit,
  schema,
  defaultValues,
  children,
  submitButtonText = "Save",
  isLoading = false
}: ModalFormProps<T>) {
  const form = useForm<T>({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  })

  const handleSubmit = async (values: T) => {   
    onSubmit(values)    
    form.reset()
  }
  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
    }
  }, [defaultValues])
  return (
    <AlertDialog open={isOpen}>
      <AlertDialogPortal>
        {isLoading && <Spinner/>}
        <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />        
        <AlertDialogContent className="sm:max-w-[500px] p-0 rounded-lg gap-1 bg-white-A700 animate-contentShow">
          <AlertDialogHeader className="flex text-left gap-4 p-4 border-b">
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="">
            <FormProvider {...form}>
              <form className="" onSubmit={e=>{
                e.preventDefault()
                form.handleSubmit(handleSubmit)()
              }
              }>
                <div className="space-y-4 h-full px-4 overflow-auto max-h-[700px]">{children(form)}</div>
                <AlertDialogFooter className="flex gap-2 items-center flex-row p-4">
                  <Button className="text-blue-700 border border-blue-700 w-[100px]" type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button disabled={
                    form.formState.isSubmitting || !form.formState.isDirty
                  } className="bg-blue-700 border border-blue-700 text-white-A700 w-[100px]" type="submit">{submitButtonText}</Button>
                </AlertDialogFooter>
              </form>
            </FormProvider>
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  )
}
